
import React, { useState, useMemo, useEffect } from 'react';
import { NewDashboardHeader } from '../../components/NewDashboardHeader';
import { Footer } from '../../components/Footer';
import { AppUser, UserRole, UserStatus } from '../../types';
import { CIEN_CUADRAS_BLUE } from '../../firebase/firebaseConfig';
import { HomeModernIcon, UserCircleIcon, CogIcon, BuildingOfficeIcon, ListIcon, ChartBarIcon } from '../../components/Icons';
import { Activity } from 'lucide-react';
import { DashboardViewProps } from '../../config/routeMappings';

// Import Section Components
import { SuperAdminPropertyManagementSection } from './sections/SuperAdminPropertyManagementSection';
import { AdminUserManagement } from './sections/AdminUserManagement'; 
import { SuperAdminAppManagementSection } from './sections/SuperAdminAppManagementSection';
import { AdminInmobiliariasManagement } from './sections/AdminInmobiliariasManagement';
import { AdminPropertySpreadsheetSection } from './sections/AdminPropertySpreadsheetSection';
import { AdminInventoryStatsSection } from './sections/AdminInventoryStatsSection';
import { AdminInventoryHealthSection } from './sections/health/AdminInventoryHealthSection';

// Import Hooks
import { useAdminUsers } from '../../hooks/useAdminUsers';
import { useUserCsvProcessor } from '../../hooks/useUserCsvProcessor';
import { UserFormModal } from './UserFormModal';
import { ProcessingProgressModal } from '../../components/ProcessingProgressModal';
import { downloadUserTemplate } from '../../utils/csvUtils';


type AdminTab = 'properties' | 'users' | 'config' | 'inmobiliarias' | 'spreadsheet' | 'stats' | 'health';

export const AdminDashboardView: React.FC<DashboardViewProps> = ({ onLogout, appUser, firebaseUser, newlyAddedRange }) => {
  const isOperacion = appUser?.role === UserRole.OPERACION;
  const [activeTab, setActiveTab] = useState<AdminTab>(isOperacion ? 'spreadsheet' : 'properties');

  // User management uses default DB
  const {
      users,
      isLoadingUsers,
      adminUsersMessage,
      setAdminUsersMessage,
      updateOpStatus,
      updateUserStatus,
      saveUser,
      refreshUsers,
  } = useAdminUsers(appUser);

  const {
    isProcessing: isProcessingUserCsv,
    processingTitle: userCsvProcessingTitle,
    processedCount: userCsvProcessedCount,
    totalToProcess: userCsvTotalToProcess,
    isComplete: isUserCsvProcessingComplete,
    processingErrorMsg: userCsvProcessingError,
    generalMessage: userCsvGeneralMessage,
    results: userCsvResults,
    processUserCsv,
    resetProcessingState: resetUserCsvState,
    downloadUserCsvResults,
  } = useUserCsvProcessor(users, appUser);

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AppUser | undefined>(undefined);
  const [userCsvFile, setUserCsvFile] = useState<File | null>(null);

  const handleOpenUserModal = (user?: AppUser) => {
      setEditingUser(user);
      setIsUserModalOpen(true);
      setAdminUsersMessage(null);
  };

  const handleSaveUser = async (userData: Partial<AppUser>, userId: string) => {
    try {
        await saveUser(userData, userId);
        setIsUserModalOpen(false);
        setAdminUsersMessage(`Usuario ${userData.username} actualizado exitosamente.`);
    } catch (e) {
        console.error("Save user failed, modal will display error.");
    }
  };

  const handleUserCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUserCsvFile(file);
    }
  };
  
  const handleProcessUserCsv = () => {
    if (userCsvFile) {
      processUserCsv(userCsvFile);
    }
  };

  const handleCloseUserCsvModal = () => {
    resetUserCsvState();
    setUserCsvFile(null);
    refreshUsers(); // Refresh user list after processing
  };

  const handleDownloadUserTemplate = () => {
    const { content, fileName } = downloadUserTemplate();
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  const TabButton: React.FC<{ tabName: AdminTab; icon: React.ReactNode; label: string }> = ({ tabName, icon, label }) => (
    <button
      onClick={() => setActiveTab(tabName)}
      className={`flex-1 sm:flex-none flex items-center justify-center sm:justify-start px-4 py-4 text-xs font-black uppercase tracking-widest border-b-4 transition-all ${
        activeTab === tabName
          ? 'border-[#0073b7] text-[#0073b7] bg-sky-50/50'
          : 'border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-50'
      }`}
      role="tab"
      aria-selected={activeTab === tabName}
    >
      {icon}
      <span className="ml-3 hidden md:block">{label}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans">
      <NewDashboardHeader onLogout={onLogout} appUser={appUser} isNewlyAddedMode={!!newlyAddedRange} />
      
      <main className="flex-grow max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
        <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-black text-gray-900 uppercase tracking-tighter leading-none">
                Panel de <br/>
                <span className="text-[#0073b7]">{isOperacion ? 'Operaciones' : 'Superadministración'}</span>
            </h1>
            <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 font-black">
                    {appUser?.displayName?.charAt(0) || 'A'}
                </div>
                <p className="text-sm text-gray-500 font-medium">Bienvenido, <span className="text-gray-900 font-bold">{appUser?.displayName || appUser?.email}</span></p>
            </div>
        </header>

        <div className="border-b border-gray-100 mb-8">
          <nav className="-mb-px flex overflow-x-auto custom-scrollbar gap-2" aria-label="Tabs">
            {!isOperacion && <TabButton tabName="properties" icon={<HomeModernIcon className="w-5 h-5"/>} label="Propiedades" />}
            {!isOperacion && <TabButton tabName="stats" icon={<ChartBarIcon className="w-5 h-5"/>} label="Estadísticas" />}
            <TabButton tabName="spreadsheet" icon={<ListIcon className="w-5 h-5"/>} label="Gestión 183150" />
            <TabButton tabName="health" icon={<Activity className="w-5 h-5"/>} label="Salud del Inventario" />
            {!isOperacion && <TabButton tabName="inmobiliarias" icon={<BuildingOfficeIcon className="w-5 h-5"/>} label="Inmobiliarias" />}
            {!isOperacion && <TabButton tabName="users" icon={<UserCircleIcon className="w-5 h-5"/>} label="Usuarios" />}
            {!isOperacion && <TabButton tabName="config" icon={<CogIcon className="w-5 h-5"/>} label="Configuración" />}
          </nav>
        </div>

        <div className="min-h-[500px]">
          {activeTab === 'properties' && !isOperacion && (
            <div className="animate-fade-in"><SuperAdminPropertyManagementSection appUser={appUser} /></div>
          )}
          {activeTab === 'stats' && !isOperacion && (
            <div className="animate-fade-in"><AdminInventoryStatsSection appUser={appUser} /></div>
          )}
          {activeTab === 'spreadsheet' && (
            <div className="animate-fade-in"><AdminPropertySpreadsheetSection /></div>
          )}
          {activeTab === 'health' && (
            <div className="animate-fade-in"><AdminInventoryHealthSection /></div>
          )}
          {activeTab === 'inmobiliarias' && !isOperacion && (
             <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm animate-fade-in">
                <AdminInmobiliariasManagement />
            </div>
          )}
          {activeTab === 'users' && !isOperacion && (
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm animate-fade-in">
                <AdminUserManagement
                    users={users}
                    isLoadingUsers={isLoadingUsers}
                    message={adminUsersMessage}
                    updateOpStatus={updateOpStatus}
                    onOpenUserModal={handleOpenUserModal}
                    onChangeUserStatus={async (userId, status, username) => {
                        const action = status === UserStatus.ACTIVE ? 'desactivar' : 'activar';
                        if (window.confirm(`¿Estás seguro de que quieres ${action} al usuario ${username}?`)) {
                            await updateUserStatus(userId, status);
                        }
                    }}
                    onRefreshUsers={refreshUsers}
                    userCsvFile={userCsvFile}
                    onUserCsvFileChange={handleUserCsvFileChange}
                    onProcessUserCsv={handleProcessUserCsv}
                    onDownloadUserTemplate={handleDownloadUserTemplate}
                    isProcessingUserCsv={isProcessingUserCsv}
                    userCsvProcessingMessage={userCsvGeneralMessage}
                    userCsvResults={userCsvResults}
                    isProcessingComplete={isUserCsvProcessingComplete}
                />
            </div>
          )}
          {activeTab === 'config' && !isOperacion && <div className="animate-fade-in"><SuperAdminAppManagementSection appUser={appUser} /></div>}
        </div>
      </main>
      
      <Footer />

      {isUserModalOpen && (
          <UserFormModal
            isOpen={isUserModalOpen}
            onClose={() => setIsUserModalOpen(false)}
            onSave={handleSaveUser}
            initialData={editingUser}
            mode={editingUser ? 'edit' : 'add'}
            existingEmails={users.map(u => u?.email || '').filter(Boolean)}
            existingUsernames={users.map(u => u?.username || '').filter(Boolean)}
            error={adminUsersMessage}
          />
      )}

      {(isProcessingUserCsv || (isUserCsvProcessingComplete && userCsvResults.length > 0)) && (
        <ProcessingProgressModal
            isOpen={true}
            onClose={handleCloseUserCsvModal}
            title={userCsvProcessingTitle}
            processedItems={userCsvProcessedCount}
            totalItems={userCsvTotalToProcess}
            isComplete={isUserCsvProcessingComplete}
            errorMessage={userCsvProcessingError}
            summaryMessage={userCsvGeneralMessage}
            results={userCsvResults}
            onDownloadResults={downloadUserCsvResults}
        />
      )}
    </div>
  );
};
